import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2, Trash2, X } from 'lucide-react'
import { collection, getDocs, limit, query, where } from 'firebase/firestore'
import { AuthModal } from '@/components/auth/AuthModal'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { MatcherFeedLocationPicker } from '@/components/matcher/MatcherFeedLocationPicker'
import { cn } from '@/lib/utils'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useAuthState } from '@/hooks/useAuth'
import { useCities } from '@/hooks/useCities'
import { db } from '@/lib/firebase'
import {
  choosePairwiseOption,
  clearSavedPairwiseSessionId,
  deleteMatcherSession,
  fetchMatcherOption,
  fetchPairwiseResults,
  getOrCreatePairwiseUserId,
  listStoredMatcherSessions,
  savePairwiseSessionId,
  saveMatcherResultsSession,
  type MatcherOption,
  type PairwiseChooseResponse,
  type StoredMatcherResultMatch,
  type StoredMatcherSession,
} from '@/lib/pairwiseMatcherApi'
import { getLocationFilterDisplay, isCountrySentinel, resolveSentinelToCities } from '@/lib/locationRegions'
import { fetchArtistsByIds } from '@/lib/tattooData'
import matcherIntroImage1 from '@/assets/images/artist_match/image_1.jpeg'
import matcherIntroImage2 from '@/assets/images/artist_match/image_2.jpg'
import type { Artist, PortfolioItem } from '@/types/models'

const TOTAL_ROUNDS = 20
const LOG = '[Matcher]'

/** Same max-width centering as `phone-container-padded`; horizontal padding uses safe-area insets so scroll bodies align with the sticky header on notched devices. */
const MATCHER_PHONE_GUTTER =
  'mx-auto w-full max-w-[calc(var(--max-content-width)+(var(--min-side-padding)*2))] md:max-w-[calc(var(--max-content-width-desktop)+(var(--min-side-padding)*2))] pl-[max(var(--min-side-padding),env(safe-area-inset-left,0px))] pr-[max(var(--min-side-padding),env(safe-area-inset-right,0px))]'

function matcherLocationDisplayLabel(raw: string, launchedCities: string[]): string {
  if (!raw) return ''
  if (!isCountrySentinel(raw)) return raw
  const launched = new Set(launchedCities)
  const listed = resolveSentinelToCities(raw)?.filter((c) => launched.has(c)) ?? []
  if (listed.length === 1) return listed[0]!
  const countryOnly = getLocationFilterDisplay(raw, '')
  return listed.length > 1 ? `${countryOnly} — all cities` : countryOnly
}

type FlowStep = 'start' | 'pairwise' | 'preferences' | 'results'
type IntentMode = 'specific' | 'exploring'

interface RankedMatch {
  artistId: string
  score: number
  rank: number
}

interface ResultCardData {
  match: RankedMatch
  artist: Artist
  previews: PortfolioItem[]
}

function getArtistDisplayName(artist: Artist): string {
  if (artist.firstName && artist.lastName) {
    return `${artist.firstName} ${artist.lastName[0]}.`
  }

  return artist.firstName || artist.displayName || 'Unknown Artist'
}

function formatSessionTimestamp(date: Date | null): string {
  if (!date) {
    return 'Saved recently'
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

/** Matches progress shown during pairwise (`stepIndex` minus skip rounds). */
function matcherSessionChoiceProgress(session: StoredMatcherSession): { completed: number; total: number } {
  const total =
    typeof session.totalRounds === 'number' && session.totalRounds > 0 ? session.totalRounds : TOTAL_ROUNDS

  if (session.status === 'completed' || session.isDone) {
    return { completed: total, total }
  }

  const skipRoundCount = Math.floor(session.skippedItemIds.length / 2)
  const completed = Math.min(total, Math.max(0, session.stepIndex - skipRoundCount))

  return { completed, total }
}

function MatcherStyledAlertDialog({
  open,
  onOpenChange,
  title,
  description,
  cancelLabel = 'Cancel',
  confirmLabel,
  confirmBusyLabel,
  isBusy,
  onConfirm,
  confirmVariant = 'primary',
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  cancelLabel?: string
  confirmLabel: string
  confirmBusyLabel: string
  isBusy: boolean
  onConfirm: () => void
  confirmVariant?: 'primary' | 'destructive'
}) {
  const titleId = useId()
  const descId = useId()

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open && !isBusy) {
        onOpenChange(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, isBusy, onOpenChange])

  if (!open) {
    return null
  }

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/50 animate-in fade-in-0"
        aria-hidden
        onClick={() => !isBusy && onOpenChange(false)}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descId}
          className="bg-background rounded-[var(--card-radius)] shadow-lg border border-border/40 max-w-sm w-full animate-in zoom-in-95 pointer-events-auto"
        >
          <div className="p-6 space-y-4 text-center">
            <h2 id={titleId} className="text-xl font-semibold text-foreground">
              {title}
            </h2>
            <p id={descId} className="text-base text-foreground leading-relaxed">
              {description}
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-3 w-full pt-2 mt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-12 font-semibold rounded-lg border-border/80"
                disabled={isBusy}
                onClick={() => onOpenChange(false)}
              >
                {cancelLabel}
              </Button>
              <Button
                type="button"
                variant={confirmVariant === 'destructive' ? 'destructive' : undefined}
                className={
                  confirmVariant === 'destructive'
                    ? 'flex-1 h-12 font-semibold rounded-lg'
                    : 'flex-1 h-12 font-semibold rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground'
                }
                disabled={isBusy}
                onClick={() => void onConfirm()}
              >
                {isBusy ? confirmBusyLabel : confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function MatcherExitDialog({
  open,
  onOpenChange,
  onLeave,
  isLeaving,
  description,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onLeave: () => void | Promise<void>
  isLeaving: boolean
  description: string
}) {
  return (
    <MatcherStyledAlertDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Leave matcher?"
      description={description}
      cancelLabel="Cancel"
      confirmLabel="Leave matcher"
      confirmBusyLabel="Leaving…"
      isBusy={isLeaving}
      onConfirm={() => void onLeave()}
      confirmVariant="primary"
    />
  )
}

function addUniqueIds(existing: string[], ids: Array<string | null | undefined>): string[] {
  const next = new Set(existing)
  ids.forEach((id) => {
    if (id) {
      next.add(id)
    }
  })
  return Array.from(next)
}

async function fetchArtistPreviews(artistId: string): Promise<PortfolioItem[]> {
  try {
    const portfolioQuery = query(
      collection(db, 'portfolioItems'),
      where('artistId', '==', artistId),
      limit(4)
    )

    const snapshot = await getDocs(portfolioQuery)
    return snapshot.docs.map((entry) => {
      const data = entry.data()
      return {
        id: entry.id,
        artistId: data.artistId,
        type: data.type || 'image',
        storagePath: data.storagePath || '',
        imageUrl: data.imageUrl,
        compressedImageUrl: data.compressedImageUrl,
        width: data.width || 0,
        height: data.height || 0,
        caption: data.caption || '',
        styleTags: data.styles || data.styleTags || [],
        createdAt: data.createdAt?.toDate?.() || new Date(),
      } satisfies PortfolioItem
    })
  } catch (error) {
    console.error('[ArtistMatchPage] Failed to load artist preview items:', error)
    return []
  }
}

type PairSlotVisual = 'shimmer' | 'image'

function pairSlotVisual(
  expectedId: string | undefined,
  option: MatcherOption | null,
): PairSlotVisual {
  if (expectedId && option?.id === expectedId && option.imageUrl) {
    return 'image'
  }
  return 'shimmer'
}

function PairChoiceCard({
  option,
  slotVisual,
  showPendingShimmerOverlay,
  disabled,
  onChoose,
}: {
  option: MatcherOption | null
  slotVisual: PairSlotVisual
  /** Pulse layer on top of the current image while the next pair loads */
  showPendingShimmerOverlay?: boolean
  disabled: boolean
  onChoose: () => void
}) {
  const [isPressed, setIsPressed] = useState(false)

  const handlePointerDown = () => {
    if (!disabled) setIsPressed(true)
  }
  const handlePointerUp = () => setIsPressed(false)
  const handlePointerLeave = () => setIsPressed(false)

  return (
    <div className="relative min-h-0 w-full min-w-0 flex-1 md:h-full">
      <button
        type="button"
        className="absolute inset-0 overflow-hidden rounded-[1.75rem] bg-muted text-left transition-all duration-150 ease-out md:rounded-2xl"
        style={{
          transform: isPressed ? 'scale(0.97)' : 'scale(1)',
        }}
        disabled={disabled}
        onClick={onChoose}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerLeave}
        onPointerCancel={handlePointerLeave}
      >
        {slotVisual === 'shimmer' ? (
          <div
            className="absolute inset-0 animate-pulse rounded-[1.75rem] bg-border/70 dark:bg-border/60 md:rounded-2xl"
            aria-hidden
          />
        ) : option?.imageUrl ? (
          <img
            src={option.imageUrl}
            alt={option.caption || 'Tattoo option'}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : null}

        {slotVisual === 'image' ? (
          <>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />
            {option?.caption ? (
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="text-sm text-white line-clamp-2">{option.caption}</p>
              </div>
            ) : null}
          </>
        ) : null}

        {showPendingShimmerOverlay ? (
          <div
            className="pointer-events-none absolute inset-0 z-[5] rounded-[1.75rem] animate-pulse bg-background/45 dark:bg-background/50 md:rounded-2xl"
            aria-hidden
          />
        ) : null}
      </button>
    </div>
  )
}

export default function ArtistMatchPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { cities } = useCities()
  const { user } = useAuthState()
  const { trackEvent } = useAnalytics()
  const isAuthenticated = !!user?.uid

  const optionCacheRef = useRef<Map<string, MatcherOption | null>>(new Map())
  const bootstrappedRef = useRef(false)
  const bootstrapInFlightRef = useRef(false)
  const initialSessionStartedRef = useRef(false)
  const preloadedBranchOptionsRef = useRef<Map<string, { a: MatcherOption | null; b: MatcherOption | null }>>(new Map())
  const isSubmittingChoiceRef = useRef(false)
  const skipNextOptionLoadRef = useRef(false)

  const [flowStep, setFlowStep] = useState<FlowStep>('start')
  const [userId, setUserId] = useState('')
  const [session, setSession] = useState<PairwiseChooseResponse | null>(null)
  const [savedSessions, setSavedSessions] = useState<StoredMatcherSession[]>([])
  const [sessionResults, setSessionResults] = useState<StoredMatcherResultMatch[]>([])

  const [isBootstrapping, setIsBootstrapping] = useState(true)
  const [isInitializing, setIsInitializing] = useState(false)
  const [isSubmittingChoice, setIsSubmittingChoice] = useState(false)
  const [isLoadingSavedSessions, setIsLoadingSavedSessions] = useState(false)
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false)
  const [isExitActionPending, setIsExitActionPending] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [saveAfterLoginRequested, setSaveAfterLoginRequested] = useState(false)
  const [sessionPendingDeleteId, setSessionPendingDeleteId] = useState<string | null>(null)
  const [isDeletingSession, setIsDeletingSession] = useState(false)

  const [pairError, setPairError] = useState<string | null>(null)
  const [resultsError, setResultsError] = useState<string | null>(null)

  const [optionA, setOptionA] = useState<MatcherOption | null>(null)
  const [optionB, setOptionB] = useState<MatcherOption | null>(null)
  const [pairOptionsLoading, setPairOptionsLoading] = useState(false)
  const [pairTransitionOverlay, setPairTransitionOverlay] = useState<{
    a: MatcherOption | null
    b: MatcherOption | null
  } | null>(null)
  const [isWaitingForNextBranches, setIsWaitingForNextBranches] = useState(false)

  const [selectedCity, setSelectedCity] = useState('')
  const [intentMode, setIntentMode] = useState<IntentMode>('exploring')
  const [ideaText, setIdeaText] = useState('')

  const [likedItemIds, setLikedItemIds] = useState<string[]>([])
  const [dislikedItemIds, setDislikedItemIds] = useState<string[]>([])
  const [skippedItemIds, setSkippedItemIds] = useState<string[]>([])
  const [skipRoundCount, setSkipRoundCount] = useState(0)
  const [optimisticStepCount, setOptimisticStepCount] = useState(0)

  const [isLoadingResults, setIsLoadingResults] = useState(false)
  const [resultCards, setResultCards] = useState<ResultCardData[]>([])
  const [cityFallbackApplied, setCityFallbackApplied] = useState(false)
  const resumeSessionId = searchParams.get('sessionId')
  const resumeUserId = searchParams.get('userId')
  const resumeSelectedCity = searchParams.get('selectedCity') || ''
  const resumeIntentMode = searchParams.get('intentMode') === 'specific' ? 'specific' : 'exploring'
  const resumeIdeaText = searchParams.get('ideaText') || ''
  const shouldResumeResults = searchParams.get('resume') === 'results' && !!resumeSessionId

  useEffect(() => {
    if (!selectedCity && cities.length > 0) {
      setSelectedCity(cities[0])
    }
  }, [cities, selectedCity])

  useEffect(() => {
    const isInActiveStep = flowStep === 'pairwise' || flowStep === 'preferences' || flowStep === 'results'
    const hasParam = searchParams.get('matcherActive') === 'true'
    if (isInActiveStep && !hasParam) {
      setSearchParams(prev => { prev.set('matcherActive', 'true'); return prev }, { replace: true })
    } else if (!isInActiveStep && hasParam) {
      setSearchParams(prev => { prev.delete('matcherActive'); return prev }, { replace: true })
    }
  }, [flowStep, searchParams, setSearchParams])

  const refreshSavedSessions = useCallback(async (resolvedUserId?: string | null) => {
    if (!resolvedUserId) {
      setSavedSessions([])
      return []
    }

    setIsLoadingSavedSessions(true)
    try {
      const sessions = await listStoredMatcherSessions(resolvedUserId)
      setSavedSessions(sessions)
      return sessions
    } catch (error) {
      console.error('[ArtistMatchPage] Failed to load saved matcher sessions:', error)
      setSavedSessions([])
      return []
    } finally {
      setIsLoadingSavedSessions(false)
    }
  }, [])

  const resetSessionDraftState = useCallback(() => {
    setPairError(null)
    setResultsError(null)
    setResultCards([])
    setSessionResults([])
    setCityFallbackApplied(false)
    setIdeaText('')
    setIntentMode('exploring')
    setLikedItemIds([])
    setDislikedItemIds([])
    setSkippedItemIds([])
    setSkipRoundCount(0)
    setOptimisticStepCount(0)
  }, [])

  /** Matcher "lobby": start screen at /tattoo-artist-matcher with no resume params (bottom-nav Matcher tab). */
  const navigateToMatcherLobby = useCallback(() => {
    clearSavedPairwiseSessionId()
    resetSessionDraftState()
    preloadedBranchOptionsRef.current.clear()
    optionCacheRef.current.clear()
    isSubmittingChoiceRef.current = false
    skipNextOptionLoadRef.current = false
    setSession(null)
    setFlowStep('start')
    setOptionA(null)
    setOptionB(null)
    setPairTransitionOverlay(null)
    setIsWaitingForNextBranches(false)
    setIsInitializing(false)
    setIsSubmittingChoice(false)
    setIsLoadingResults(false)
    setPairError(null)
    setResultsError(null)
    setUserId(getOrCreatePairwiseUserId(user?.uid))
    navigate({ pathname: '/tattoo-artist-matcher', search: '' }, { replace: true })
    if (user?.uid) {
      void refreshSavedSessions(user.uid)
    }
  }, [navigate, refreshSavedSessions, resetSessionDraftState, user?.uid])

  const handleDeleteSession = useCallback(async (sessionId: string) => {
    if (!user?.uid) return

    setIsDeletingSession(true)
    try {
      await deleteMatcherSession({ sessionId, ownerUserId: user.uid })
      await refreshSavedSessions(user.uid)
    } catch (error) {
      console.error('[ArtistMatchPage] Failed to delete session:', error)
    } finally {
      setIsDeletingSession(false)
      setSessionPendingDeleteId(null)
    }
  }, [refreshSavedSessions, user?.uid])

  const loadOptionFromCacheOrDb = useCallback(async (optionId: string): Promise<MatcherOption | null> => {
    const cached = optionCacheRef.current.get(optionId)
    if (cached !== undefined) {
      console.log(`${LOG}[cache-hit] optionId=${optionId}`)
      return cached
    }

    console.log(`${LOG}[firestore-fetch] optionId=${optionId}`)
    const t0 = performance.now()
    const option = await fetchMatcherOption(optionId)
    console.log(`${LOG}[firestore-fetch-done] optionId=${optionId} found=${!!option} ms=${(performance.now() - t0).toFixed(0)}`)
    optionCacheRef.current.set(optionId, option)
    return option
  }, [])

  const loadCurrentPairOptions = useCallback(async (payload: PairwiseChooseResponse | null) => {
    if (!payload?.currentPair) {
      console.log(`${LOG}[load-pair] no currentPair — clearing options`)
      setPairOptionsLoading(false)
      setOptionA(null)
      setOptionB(null)
      return
    }

    const { optionAId, optionBId } = payload.currentPair
    console.log(`${LOG}[load-pair] start a=${optionAId} b=${optionBId}`)
    setOptionA((prev) => (prev?.id === optionAId ? prev : null))
    setOptionB((prev) => (prev?.id === optionBId ? prev : null))
    setPairOptionsLoading(true)
    const t0 = performance.now()
    try {
      const [a, b] = await Promise.all([
        loadOptionFromCacheOrDb(optionAId),
        loadOptionFromCacheOrDb(optionBId),
      ])
      console.log(`${LOG}[load-pair] done a=${optionAId} (found=${!!a}) b=${optionBId} (found=${!!b}) ms=${(performance.now() - t0).toFixed(0)}`)
      setOptionA(a)
      setOptionB(b)
    } finally {
      setPairOptionsLoading(false)
    }
  }, [loadOptionFromCacheOrDb])

  const preloadBranchOptions = useCallback(async (payload: PairwiseChooseResponse | null) => {
    if (!payload?.nextByChosenId?.length) {
      preloadedBranchOptionsRef.current.clear()
      console.log(`${LOG}[preload] no branches to preload`)
      return
    }

    preloadedBranchOptionsRef.current.clear()
    const branchCount = payload.nextByChosenId.filter((b) => b.nextPair).length
    console.log(`${LOG}[preload] starting preload for ${branchCount} branches`)
    const t0 = performance.now()

    const loadPromises = payload.nextByChosenId.map(async (branch) => {
      if (!branch.nextPair) return

      const [optA, optB] = await Promise.all([
        loadOptionFromCacheOrDb(branch.nextPair.optionAId),
        loadOptionFromCacheOrDb(branch.nextPair.optionBId),
      ])
      preloadedBranchOptionsRef.current.set(branch.chosenId, { a: optA, b: optB })
      console.log(`${LOG}[preload] branch chosenId=${branch.chosenId} a=${branch.nextPair.optionAId}(found=${!!optA}) b=${branch.nextPair.optionBId}(found=${!!optB})`)

      if (optA?.imageUrl) {
        const img = new Image()
        img.src = optA.imageUrl
      }
      if (optB?.imageUrl) {
        const img = new Image()
        img.src = optB.imageUrl
      }
    })

    await Promise.all(loadPromises)
    console.log(`${LOG}[preload] all branches done ms=${(performance.now() - t0).toFixed(0)}`)
  }, [loadOptionFromCacheOrDb])

  const buildResultCards = useCallback(async (rankedMatches: RankedMatch[]) => {
    const artists = await fetchArtistsByIds(rankedMatches.map((item) => item.artistId))
    const artistById = new Map(artists.map((artist) => [artist.id, artist]))

    const cardsWithArtist: Array<{ match: RankedMatch; artist: Artist }> = rankedMatches
      .map((match) => {
        const artist = artistById.get(match.artistId)
        if (!artist) {
          return null
        }
        return { match, artist }
      })
      .filter((entry): entry is { match: RankedMatch; artist: Artist } => entry !== null)

    return Promise.all(
      cardsWithArtist.map(async (entry) => ({
        match: entry.match,
        artist: entry.artist,
        previews: await fetchArtistPreviews(entry.artist.id),
      } satisfies ResultCardData))
    )
  }, [])

  const hydrateStoredResults = useCallback(async (matches: StoredMatcherResultMatch[]) => {
    console.log(`${LOG}[hydrate] hydrating ${matches.length} stored result matches`)
    setIsLoadingResults(true)
    setResultsError(null)
    try {
      const t0 = performance.now()
      const cards = await buildResultCards(matches.map((entry) => ({
        artistId: entry.artistId,
        score: entry.score,
        rank: entry.rank,
      })))
      console.log(`${LOG}[hydrate] done — cards=${cards.length} ms=${(performance.now() - t0).toFixed(0)}`)
      setResultCards(cards)
      setSessionResults(matches)
    } catch (error) {
      console.error(`${LOG}[hydrate] FAILED:`, error)
      setResultsError('Could not load saved matches. Please try again.')
    } finally {
      setIsLoadingResults(false)
    }
  }, [buildResultCards])

  const initializeNewSession = useCallback(async (resolvedUserId?: string) => {
    const effectiveUserId = resolvedUserId ?? getOrCreatePairwiseUserId(user?.uid)

    console.log(`${LOG}[init] starting new session userId=${effectiveUserId}`)
    setIsInitializing(true)
    setFlowStep('pairwise')
    setUserId(effectiveUserId)
    setPairTransitionOverlay(null)
    resetSessionDraftState()
    setSelectedCity((current) => current || cities[0] || '')
    clearSavedPairwiseSessionId()

    try {
      console.log(`${LOG}[init] POST choose (new session)`)
      const t0 = performance.now()
      const payload = await choosePairwiseOption({
        userId: effectiveUserId,
        sessionId: null,
        chosenId: null,
        totalRounds: TOTAL_ROUNDS,
        currentOptionAId: null,
        currentOptionBId: null,
      })
      console.log(`${LOG}[init] POST choose done ms=${(performance.now() - t0).toFixed(0)} sessionId=${payload.sessionId} stepIndex=${payload.stepIndex} done=${payload.done} currentPair=${JSON.stringify(payload.currentPair)}`)

      savePairwiseSessionId(payload.sessionId)
      setSession(payload)

      void trackEvent('matcher_session_started', {
        session_id: payload.sessionId,
        is_authenticated: !!user?.uid,
      })

      if (user?.uid) {
        await refreshSavedSessions(user.uid)
      }
    } catch (error) {
      console.error(`${LOG}[init] FAILED to initialize session:`, error)
      setPairError('Could not load matcher. Please try again.')
    } finally {
      setIsInitializing(false)
      setIsBootstrapping(false)
      bootstrappedRef.current = true
      console.log(`${LOG}[init] done — isInitializing=false isBootstrapping=false`)
    }
  }, [cities, refreshSavedSessions, resetSessionDraftState, trackEvent, user?.uid])

  useEffect(() => {
    let isCancelled = false

    const bootstrap = async () => {
      if (bootstrapInFlightRef.current) {
        return
      }

      bootstrapInFlightRef.current = true

      try {
      if (!bootstrappedRef.current) {
        if (shouldResumeResults) {
          setIsBootstrapping(false)
          bootstrappedRef.current = true
          initialSessionStartedRef.current = true
          await restoreResultsFromSearchParams()
          return
        }

        const resolvedMatcherUserId = getOrCreatePairwiseUserId(user?.uid)
        setUserId(resolvedMatcherUserId)

        if (!user?.uid) {
          setSavedSessions([])
          setFlowStep('start')
          setIsBootstrapping(false)
          bootstrappedRef.current = true
          return
        }

        await refreshSavedSessions(user.uid)
        if (isCancelled) {
          return
        }

        setFlowStep('start')
        setIsBootstrapping(false)
        bootstrappedRef.current = true
        return
      }

      if (user?.uid) {
        await refreshSavedSessions(user.uid)
      }
      } finally {
        bootstrapInFlightRef.current = false
      }
    }

    void bootstrap()

    return () => {
      isCancelled = true
    }
  }, [initializeNewSession, refreshSavedSessions, shouldResumeResults, user?.uid])

  useEffect(() => {
    if (!saveAfterLoginRequested || !user?.uid || !session || sessionResults.length === 0) {
      return
    }

    void (async () => {
      await saveMatcherResultsSession({
        sessionId: session.sessionId,
        ownerUserId: user.uid,
        selectedCity,
        intentMode,
        ideaText,
      })
      await refreshSavedSessions(user.uid)
      setSaveAfterLoginRequested(false)
      setShowAuthModal(false)
    })()
  }, [ideaText, intentMode, refreshSavedSessions, saveAfterLoginRequested, selectedCity, session, sessionResults, user?.uid])

  useEffect(() => {
    if (skipNextOptionLoadRef.current) {
      skipNextOptionLoadRef.current = false
      console.log(`${LOG}[session-effect] skipping loadCurrentPairOptions (preloaded branch already applied) sessionId=${session?.sessionId} stepIndex=${session?.stepIndex}`)
    } else {
      console.log(`${LOG}[session-effect] session changed → loadCurrentPairOptions sessionId=${session?.sessionId} stepIndex=${session?.stepIndex} currentPair=${JSON.stringify(session?.currentPair)}`)
      void loadCurrentPairOptions(session)
    }
    void preloadBranchOptions(session)
  }, [loadCurrentPairOptions, preloadBranchOptions, session])

  useEffect(() => {
    if (!pairTransitionOverlay) {
      return
    }
    if (!session?.currentPair) {
      return
    }
    const ready =
      optionA?.id === session.currentPair.optionAId &&
      optionB?.id === session.currentPair.optionBId &&
      !!optionA?.imageUrl &&
      !!optionB?.imageUrl
    if (ready) {
      setPairTransitionOverlay(null)
    }
  }, [optionA, optionB, pairTransitionOverlay, session])

  const actualChoiceCount = useMemo(() => {
    const serverStepIndex = session?.stepIndex ?? 0
    const effectiveSteps = Math.max(serverStepIndex, optimisticStepCount)
    return Math.min(TOTAL_ROUNDS, Math.max(0, effectiveSteps - skipRoundCount))
  }, [session, skipRoundCount, optimisticStepCount])

  const progressValue = useMemo(() => {
    return Math.round((actualChoiceCount / TOTAL_ROUNDS) * 100)
  }, [actualChoiceCount])

  const openSavedSession = useCallback(async (storedSession: StoredMatcherSession) => {
    void trackEvent('matcher_session_resumed', {
      session_id: storedSession.sessionId,
      session_status: storedSession.status,
    })

    setUserId(storedSession.matcherUserId)
    savePairwiseSessionId(storedSession.sessionId)
    setSelectedCity(storedSession.selectedCity || cities[0] || '')
    setIntentMode(storedSession.intentMode)
    setIdeaText(storedSession.ideaText)
    setLikedItemIds(storedSession.likedItemIds)
    setDislikedItemIds(storedSession.dislikedItemIds)
    setSkippedItemIds(storedSession.skippedItemIds)
    setSkipRoundCount(Math.floor(storedSession.skippedItemIds.length / 2))
    setSessionResults(storedSession.resultSnapshot)
    setPairError(null)
    setResultsError(null)
    setCityFallbackApplied(false)
    setPairTransitionOverlay(null)

    const restoredSession: PairwiseChooseResponse = {
      sessionId: storedSession.sessionId,
      userId: storedSession.matcherUserId,
      stepIndex: storedSession.stepIndex,
      round: storedSession.currentRound,
      totalRounds: storedSession.totalRounds,
      done: storedSession.isDone,
      currentPair: storedSession.currentPair,
      nextByChosenId: storedSession.nextByChosenId,
    }

    setSession(restoredSession)

    if (storedSession.status === 'completed') {
      setFlowStep('results')
      if (storedSession.resultSnapshot.length > 0) {
        await hydrateStoredResults(storedSession.resultSnapshot)
      } else {
        setResultCards([])
      }
      return
    }

    setResultCards([])
    if (storedSession.flowStep === 'preferences' || !storedSession.currentPair) {
      setFlowStep('preferences')
    } else {
      setFlowStep('pairwise')
    }
  }, [cities, hydrateStoredResults, trackEvent])

  const handleChooseOption = useCallback(async (chosenId: string) => {
    if (!session?.currentPair || !userId || isSubmittingChoiceRef.current) {
      console.log(`${LOG}[choose] BLOCKED chosenId=${chosenId} reason=${!session?.currentPair ? 'no-pair' : !userId ? 'no-userId' : 'already-submitting'}`)
      return
    }

    isSubmittingChoiceRef.current = true
    setIsSubmittingChoice(true)
    setIsWaitingForNextBranches(true)
    setPairTransitionOverlay({ a: optionA, b: optionB })
    setPairError(null)

    const isSkip = chosenId === 'skip'
    const nextSkipRoundCount = isSkip ? skipRoundCount + 1 : skipRoundCount
    const adjustedTotalRounds = TOTAL_ROUNDS + nextSkipRoundCount

    console.log(`${LOG}[choose] START chosenId=${chosenId} isSkip=${isSkip} sessionId=${session.sessionId} stepIndex=${session.stepIndex} a=${session.currentPair.optionAId} b=${session.currentPair.optionBId} adjustedTotal=${adjustedTotalRounds}`)

    const nextLiked = isSkip
      ? likedItemIds
      : addUniqueIds(likedItemIds, [chosenId])
    const nextDisliked = isSkip
      ? dislikedItemIds
      : addUniqueIds(dislikedItemIds, [
          chosenId === session.currentPair.optionAId ? session.currentPair.optionBId : session.currentPair.optionAId,
        ])
    const nextSkipped = isSkip
      ? addUniqueIds(skippedItemIds, [session.currentPair.optionAId, session.currentPair.optionBId])
      : skippedItemIds

    setSkipRoundCount(nextSkipRoundCount)
    setLikedItemIds(nextLiked)
    setDislikedItemIds(nextDisliked)
    setSkippedItemIds(nextSkipped)
    setOptimisticStepCount((prev) => prev + 1)

    const preloadedBranch = session.nextByChosenId?.find((b) => b.chosenId === chosenId)
    const preloadedNextPair = preloadedBranch?.nextPair ?? null
    const preloadedImages = preloadedBranchOptionsRef.current.get(chosenId)

    const didApplyPreloadedPair = !!(preloadedNextPair && preloadedImages)
    console.log(`${LOG}[choose] preload check: hasNextPair=${!!preloadedNextPair} hasImages=${!!preloadedImages} willApply=${didApplyPreloadedPair}`)
    if (didApplyPreloadedPair) {
      setOptionA(preloadedImages.a)
      setOptionB(preloadedImages.b)
      console.log(`${LOG}[choose] applied preloaded images a=${preloadedNextPair!.optionAId} b=${preloadedNextPair!.optionBId}`)
    }

    try {
      console.log(`${LOG}[choose] POST /choose → sending request`)
      const t0 = performance.now()
      const payload = await choosePairwiseOption({
        userId,
        sessionId: session.sessionId,
        chosenId,
        totalRounds: adjustedTotalRounds,
        currentOptionAId: session.currentPair.optionAId,
        currentOptionBId: session.currentPair.optionBId,
        ownerUserId: user?.uid ?? null,
        selectedCity,
        intentMode,
        ideaText,
      })
      const elapsed = (performance.now() - t0).toFixed(0)
      console.log(`${LOG}[choose] POST /choose ← response received ms=${elapsed} stepIndex=${payload.stepIndex} done=${payload.done} currentPair=${JSON.stringify(payload.currentPair)} nextByChosenId=${payload.nextByChosenId?.length ?? 0} branches`)

      savePairwiseSessionId(payload.sessionId)
      setOptimisticStepCount(payload.stepIndex)

      if (didApplyPreloadedPair && payload.currentPair) {
        const pairMatchesPreload =
          payload.currentPair.optionAId === preloadedNextPair!.optionAId &&
          payload.currentPair.optionBId === preloadedNextPair!.optionBId
        if (pairMatchesPreload) {
          skipNextOptionLoadRef.current = true
          console.log(`${LOG}[choose] server pair matches preload — skipping loadCurrentPairOptions`)
        } else {
          console.log(`${LOG}[choose] server pair DIFFERS from preload (preload: a=${preloadedNextPair!.optionAId} b=${preloadedNextPair!.optionBId}) — will reload from Firestore`)
        }
      }

      setSession(payload)
      console.log(`${LOG}[choose] setSession done — setIsSubmittingChoice(false) next`)

      const currentStepIndex = payload.stepIndex
      const currentTotalChoices = currentStepIndex - nextSkipRoundCount

      if (isSkip) {
        void trackEvent('matcher_choice_skipped', {
          session_id: payload.sessionId,
          step_index: currentStepIndex,
          total_skips: nextSkipRoundCount,
        })
      } else {
        void trackEvent('matcher_choice_made', {
          session_id: payload.sessionId,
          step_index: currentStepIndex,
          chosen_option_id: chosenId,
          total_choices: currentTotalChoices,
          total_skips: nextSkipRoundCount,
        })
      }

      if (payload.done) {
        const skipRate = currentStepIndex > 0
          ? Math.round((nextSkipRoundCount / currentStepIndex) * 100) / 100
          : 0
        console.log(`${LOG}[choose] session DONE — transitioning to preferences step`)
        setPairTransitionOverlay(null)
        void trackEvent('matcher_pairwise_completed', {
          session_id: payload.sessionId,
          total_choices: currentTotalChoices,
          total_skips: nextSkipRoundCount,
          skip_rate: skipRate,
        })
      }

      const nextFlowStep: FlowStep = payload.done ? 'preferences' : 'pairwise'
      setFlowStep(nextFlowStep)
    } catch (error) {
      console.error(`${LOG}[choose] FAILED — rolling back optimistic state`, error)
      setPairError('Choice could not be submitted. Tap again to continue.')
      setPairTransitionOverlay(null)
      setOptimisticStepCount((prev) => Math.max(0, prev - 1))
      if (isSkip) {
        setSkipRoundCount(skipRoundCount)
      }
    } finally {
      isSubmittingChoiceRef.current = false
      setIsSubmittingChoice(false)
      setIsWaitingForNextBranches(false)
      console.log(`${LOG}[choose] finally — isSubmittingChoice=false isWaiting=false`)
    }
  }, [dislikedItemIds, ideaText, intentMode, likedItemIds, optionA, optionB, selectedCity, session, skipRoundCount, skippedItemIds, trackEvent, user?.uid, userId])

  const loadResultsForCurrentSession = useCallback(async (payload?: {
    sessionId: string
    userId: string
    selectedCity: string
    intentMode?: IntentMode
    ideaText?: string
  }) => {
    const effectiveSessionId = payload?.sessionId ?? session?.sessionId
    const effectiveUserId = payload?.userId ?? userId
    const effectiveCity = payload?.selectedCity ?? selectedCity
    const effectiveIntentMode = payload?.intentMode ?? intentMode
    const effectiveIdeaText = payload?.ideaText ?? ideaText

    if (!effectiveSessionId || !effectiveUserId) {
      console.warn(`${LOG}[results] missing sessionId or userId — aborting`)
      return
    }

    console.log(`${LOG}[results] loading results sessionId=${effectiveSessionId} city=${effectiveCity} intentMode=${effectiveIntentMode}`)
    setIsLoadingResults(true)
    setResultsError(null)
    setCityFallbackApplied(false)

    try {
      console.log(`${LOG}[results] POST /result (city=${effectiveCity || 'none'})`)
      const t0 = performance.now()
      const response = await fetchPairwiseResults({
        sessionId: effectiveSessionId,
        userId: effectiveUserId,
        ownerUserId: user?.uid ?? null,
        city: effectiveCity || undefined,
        intentMode: effectiveIntentMode,
        ideaText: effectiveIdeaText,
        topK: 5,
        recompute: false,
      })
      console.log(`${LOG}[results] POST /result ← ms=${(performance.now() - t0).toFixed(0)} matches=${response.topMatches.length}`)

      let responseMatches = response.topMatches
      let didFallbackToAllCities = false

      if (effectiveCity && responseMatches.length === 0) {
        console.log(`${LOG}[results] city filter returned 0 results — retrying without city filter`)
        const t1 = performance.now()
        const fallbackResponse = await fetchPairwiseResults({
          sessionId: effectiveSessionId,
          userId: effectiveUserId,
          ownerUserId: user?.uid ?? null,
          city: undefined,
          intentMode: effectiveIntentMode,
          ideaText: effectiveIdeaText,
          topK: 5,
          recompute: false,
        })
        console.log(`${LOG}[results] POST /result (no-city fallback) ← ms=${(performance.now() - t1).toFixed(0)} matches=${fallbackResponse.topMatches.length}`)
        responseMatches = fallbackResponse.topMatches
        if (fallbackResponse.topMatches.length > 0) {
          didFallbackToAllCities = true
          setCityFallbackApplied(true)
        }
      }

      const rankedMatches: RankedMatch[] = responseMatches.slice(0, 5).map((item, index) => ({
        artistId: item.artistId,
        score: item.score,
        rank: index + 1,
      }))

      console.log(`${LOG}[results] building result cards for ${rankedMatches.length} artists`)
      const t2 = performance.now()
      const cards = await buildResultCards(rankedMatches)
      console.log(`${LOG}[results] result cards built ms=${(performance.now() - t2).toFixed(0)} cards=${cards.length}`)
      const storedMatches = rankedMatches.map((match) => ({
        artistId: match.artistId,
        score: match.score,
        rank: match.rank,
      }))

      setResultCards(cards)
      setSessionResults(storedMatches)

      void trackEvent('matcher_results_viewed', {
        session_id: effectiveSessionId,
        result_count: cards.length,
        city: effectiveCity,
        city_fallback_applied: didFallbackToAllCities,
        intent_mode: effectiveIntentMode,
      })

      if (session) {
        const completedSnapshot: PairwiseChooseResponse = {
          ...session,
          sessionId: effectiveSessionId,
          userId: effectiveUserId,
          done: true,
          currentPair: null,
        }

        setSession(completedSnapshot)
      }

      if (user?.uid) {
        await refreshSavedSessions(user.uid)
      }
    } catch (error) {
      console.error(`${LOG}[results] FAILED to load results:`, error)
      setResultsError('Could not load matches. Please try again.')
    } finally {
      setIsLoadingResults(false)
      console.log(`${LOG}[results] done — isLoadingResults=false`)
    }
  }, [buildResultCards, ideaText, intentMode, refreshSavedSessions, selectedCity, session, trackEvent, user?.uid, userId])

  async function restoreResultsFromSearchParams() {
    if (!resumeSessionId) {
      return false
    }

    const resolvedMatcherUserId = resumeUserId || getOrCreatePairwiseUserId(user?.uid)

    setUserId(resolvedMatcherUserId)
    setSelectedCity(resumeSelectedCity)
    setIntentMode(resumeIntentMode)
    setIdeaText(resumeIdeaText)
    setPairError(null)
    setResultsError(null)
    setCityFallbackApplied(false)
    setSession({
      sessionId: resumeSessionId,
      userId: resolvedMatcherUserId,
      stepIndex: TOTAL_ROUNDS,
      round: TOTAL_ROUNDS,
      totalRounds: TOTAL_ROUNDS,
      done: true,
      currentPair: null,
      nextByChosenId: [],
    })
    setFlowStep('results')

    await loadResultsForCurrentSession({
      sessionId: resumeSessionId,
      userId: resolvedMatcherUserId,
      selectedCity: resumeSelectedCity,
      intentMode: resumeIntentMode,
      ideaText: resumeIdeaText,
    })

    return true
  }

  const goToResults = useCallback(async () => {
    if (session?.sessionId) {
      void trackEvent('matcher_preferences_submitted', {
        session_id: session.sessionId,
        city: selectedCity,
        intent_mode: intentMode,
        has_idea_text: ideaText.trim().length > 0,
      })
    }

    setFlowStep('results')
    await loadResultsForCurrentSession()
  }, [ideaText, intentMode, loadResultsForCurrentSession, selectedCity, session?.sessionId, trackEvent])

  const buildMatcherNavigationParams = useCallback(() => {
    const params = new URLSearchParams({
      matcherSessionId: session?.sessionId || '',
      matcherUserId: userId,
      matcherSelectedCity: selectedCity,
      matcherIntentMode: intentMode,
    })

    if (ideaText.trim()) {
      params.set('matcherIdeaText', ideaText.trim())
    }

    return params
  }, [ideaText, intentMode, selectedCity, session?.sessionId, userId])

  const exitToMatcherLobby = useCallback(async () => {
    setIsExitActionPending(true)
    try {
      if (session?.sessionId) {
        void trackEvent('matcher_session_exited', {
          session_id: session.sessionId,
          step_index: session.stepIndex,
          flow_step: flowStep,
        })
      }

      setIsExitDialogOpen(false)
      navigateToMatcherLobby()
    } finally {
      setIsExitActionPending(false)
    }
  }, [flowStep, navigateToMatcherLobby, session?.sessionId, session?.stepIndex, trackEvent])

  const handleExitButtonClick = useCallback(() => {
    if (actualChoiceCount === 0) {
      navigateToMatcherLobby()
      return
    }
    setIsExitDialogOpen(true)
  }, [actualChoiceCount, navigateToMatcherLobby])

  const renderTopBar = (title: string, subtitle: string, showProgress = false) => (
    <div
      className={cn(
        'sticky-header-notch-backdrop sticky top-0 shrink-0 md:top-[52px] z-40 w-full',
        'bg-background/95 backdrop-blur-md border-b border-border/50 dark:border-border/30 rounded-b-2xl',
      )}
    >
      <div className={cn(MATCHER_PHONE_GUTTER, 'pt-0 pb-3 space-y-2')}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
            <p className="text-foreground mt-1">{subtitle}</p>
          </div>
          <button
            type="button"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-background text-foreground shadow-sm"
            onClick={handleExitButtonClick}
            aria-label="Close matcher"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {showProgress ? (
          <>
            <Progress value={progressValue} />
            <p className="text-xs text-muted-foreground">
              {session ? `${actualChoiceCount} / ${TOTAL_ROUNDS}` : 'Starting...'}
            </p>
          </>
        ) : null}
      </div>
    </div>
  )

  if (isBootstrapping || (flowStep === 'pairwise' && isInitializing)) {
    const loadingTopBar =
      flowStep === 'pairwise' && isInitializing
        ? renderTopBar('1. Finding your style', 'Select the tattoo you like more.', true)
        : renderTopBar('Tattoo matcher', 'Find your style.', true)

    return (
      <div
        className={cn(
          'flex flex-col overflow-hidden bg-background transition-colors',
          'h-[100dvh] md:h-[calc(100dvh-57px)]',
          isBootstrapping && 'pb-bottom-nav',
        )}
      >
        {loadingTopBar}
        <div className={cn('flex min-h-0 min-w-0 w-full flex-1 flex-col pb-4', MATCHER_PHONE_GUTTER)}>
          <span className="sr-only" role="status">
            Loading tattoos
          </span>
          <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col gap-2 md:grid md:min-h-0 md:grid-cols-2 md:grid-rows-[1fr_auto]">
            <div className="flex min-h-0 min-w-0 w-full flex-1 md:col-start-1 md:row-start-1 md:h-full md:min-h-0 md:flex-none">
              <div className="relative min-h-0 w-full min-w-0 flex-1 md:h-full">
                <div
                  className="absolute inset-0 overflow-hidden rounded-[1.75rem] bg-muted md:rounded-2xl"
                  aria-hidden
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-muted via-muted/50 to-muted bg-[length:200%_100%] animate-shimmer dark:via-muted/40" />
                </div>
              </div>
            </div>

            <div className="flex w-full min-w-0 flex-none md:col-span-2 md:row-start-2">
              <button
                type="button"
                className="flex h-14 w-full cursor-default items-center justify-center gap-2 rounded-2xl bg-card text-base font-medium text-muted-foreground pointer-events-none"
                disabled
                aria-busy
              >
                <Loader2 className="h-5 w-5 shrink-0 animate-spin text-muted-foreground motion-reduce:animate-none" aria-hidden />
                <span className="sr-only">Loading choices</span>
              </button>
            </div>

            <div className="flex min-h-0 min-w-0 w-full flex-1 md:col-start-2 md:row-start-1 md:h-full md:min-h-0 md:flex-none">
              <div className="relative min-h-0 w-full min-w-0 flex-1 md:h-full">
                <div
                  className="absolute inset-0 overflow-hidden rounded-[1.75rem] bg-muted md:rounded-2xl"
                  aria-hidden
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-muted via-muted/50 to-muted bg-[length:200%_100%] animate-shimmer dark:via-muted/40" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (flowStep === 'start') {
    /** Match ProfilePage logged-out: center until the user is signed in with at least one saved session (or while sessions are loading). */
    const showExpandedStartWithSessions =
      isAuthenticated && (isLoadingSavedSessions || savedSessions.length > 0)

    const startIntro = (
      <>
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="text-2xl font-semibold text-foreground">Tattoo matcher</h1>
          <p className="text-foreground leading-relaxed">
            {`Pick which tattoo you prefer each round. When you're done, we'll suggest artists that match your vibe!`}
          </p>
        </div>

        <div className="flex w-full items-center gap-3 sm:gap-4">
          <div className="aspect-square min-h-0 flex-1 overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/10 dark:ring-white/10">
            <img
              src={matcherIntroImage1}
              alt=""
              className="h-full w-full object-cover"
              decoding="async"
            />
          </div>
          <span className="shrink-0 select-none text-4xl leading-none" aria-hidden>
            🤔
          </span>
          <div className="aspect-square min-h-0 flex-1 overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/10 dark:ring-white/10">
            <img
              src={matcherIntroImage2}
              alt=""
              className="h-full w-full object-cover"
              decoding="async"
            />
          </div>
        </div>

        <Button
          className="h-12 w-full rounded-2xl"
          onClick={() => void initializeNewSession(userId || undefined)}
        >
          Start Matcher!
        </Button>
        {!isAuthenticated ? (
          <p className="text-left text-xs text-muted-foreground">
            Sign in from your profile to save sessions.
          </p>
        ) : null}
      </>
    )

    return (
      <div
        className={cn(
          'bg-background pb-bottom-nav',
          showExpandedStartWithSessions
            ? 'flex h-screen-mobile min-h-0 flex-col gap-3'
            : cn(
                'min-h-screen w-full transition-colors',
                'flex flex-col items-center justify-center py-8',
                'pt-safe-native-top md:pt-4',
              ),
        )}
      >
        {showExpandedStartWithSessions ? (
          <>
            <div
              className={cn(
                'phone-container-padded flex w-full shrink-0 flex-col gap-3 pt-safe-native-top-cushion md:pt-4',
              )}
            >
              {startIntro}
            </div>

            <div
              className={cn(
                'phone-container-padded flex min-h-0 min-w-0 w-full flex-1 flex-col gap-3 overflow-y-auto pb-safe [-webkit-overflow-scrolling:touch]',
              )}
            >
              <h2 className="text-base font-semibold text-foreground">Your sessions</h2>

              {isLoadingSavedSessions ? (
                <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
                  Loading saved sessions…
                </div>
              ) : null}

              {!isLoadingSavedSessions && savedSessions.length === 0 ? (
                <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
                  No saved sessions yet.
                </div>
              ) : null}

              {!isLoadingSavedSessions && savedSessions.length > 0 ? (
                <div className="flex flex-col gap-1">
                  {savedSessions.map((savedSession) => {
                    const { completed, total } = matcherSessionChoiceProgress(savedSession)
                    const primaryLabel =
                      savedSession.status === 'completed'
                        ? 'Completed session'
                        : `In progress ${completed}/${total}`

                    return (
                      <div
                        key={savedSession.sessionId}
                        className="flex items-stretch overflow-hidden rounded-lg bg-card transition-colors hover:bg-muted/50"
                      >
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 items-start gap-3 px-4 py-3.5 text-left"
                          onClick={() => void openSavedSession(savedSession)}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground">{primaryLabel}</p>
                            <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                              {savedSession.status === 'completed' && savedSession.selectedCity ? (
                                <>
                                  {matcherLocationDisplayLabel(savedSession.selectedCity, cities)}
                                  {' · '}
                                </>
                              ) : null}
                              {formatSessionTimestamp(savedSession.updatedAt || savedSession.createdAt)}
                            </p>
                          </div>
                          <span className="shrink-0 self-center pt-0.5 text-xs font-medium text-muted-foreground">
                            {savedSession.status === 'completed' ? 'Results' : 'Continue'}
                          </span>
                        </button>
                        <button
                          type="button"
                          className="flex shrink-0 items-center justify-center px-3 text-muted-foreground transition-colors hover:bg-muted/70 hover:text-destructive"
                          onClick={() => setSessionPendingDeleteId(savedSession.sessionId)}
                          aria-label="Delete session"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <div className="phone-container-padded flex w-full flex-col gap-3 text-left">
            {startIntro}
          </div>
        )}

        <MatcherStyledAlertDialog
          open={!!sessionPendingDeleteId}
          onOpenChange={(open) => { if (!open) setSessionPendingDeleteId(null) }}
          title="Delete session?"
          description="This session will be archived and removed from your list."
          cancelLabel="Cancel"
          confirmLabel="Delete"
          confirmBusyLabel="Deleting…"
          isBusy={isDeletingSession}
          onConfirm={() => { if (sessionPendingDeleteId) void handleDeleteSession(sessionPendingDeleteId) }}
          confirmVariant="destructive"
        />
      </div>
    )
  }

  if (flowStep === 'pairwise') {
    const pairImagesReady =
      !!session?.currentPair &&
      optionA?.id === session.currentPair.optionAId &&
      optionB?.id === session.currentPair.optionBId &&
      !!optionA?.imageUrl &&
      !!optionB?.imageUrl

    const cardsDisabled =
      isInitializing || isSubmittingChoice || !session?.currentPair || !pairImagesReady

    const neitherWorking =
      isInitializing ||
      isWaitingForNextBranches ||
      pairOptionsLoading ||
      (!!session?.currentPair && !pairImagesReady)

    const overlayFrozen = pairTransitionOverlay
    const displayA = overlayFrozen ? overlayFrozen.a : optionA
    const displayB = overlayFrozen ? overlayFrozen.b : optionB
    const slotVisualA = overlayFrozen
      ? displayA?.imageUrl
        ? 'image'
        : 'shimmer'
      : pairSlotVisual(session?.currentPair?.optionAId, optionA)
    const slotVisualB = overlayFrozen
      ? displayB?.imageUrl
        ? 'image'
        : 'shimmer'
      : pairSlotVisual(session?.currentPair?.optionBId, optionB)
    const showPendingShimmerA = !!overlayFrozen && slotVisualA === 'image'
    const showPendingShimmerB = !!overlayFrozen && slotVisualB === 'image'

    return (
      <>
        <div
          className="flex flex-col overflow-hidden bg-background transition-colors h-[100dvh] md:h-[calc(100dvh-57px)]"
        >
          {renderTopBar('1. Finding your style', 'Select the tattoo you like more.', true)}

          {pairError ? (
            <div className="flex-none mx-4 mb-2 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              <p>{pairError}</p>
              <Button className="mt-3" variant="outline" onClick={() => void initializeNewSession(userId || undefined)}>
                Retry
              </Button>
            </div>
          ) : null}

          <div className={cn('flex min-h-0 min-w-0 w-full flex-1 flex-col pb-4', MATCHER_PHONE_GUTTER)}>
            {/* Mobile: column (A, button, B). Desktop: 2-col grid with button spanning full width below */}
            <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col gap-2 md:grid md:min-h-0 md:grid-cols-2 md:grid-rows-[1fr_auto]">
              <div className="flex min-h-0 min-w-0 w-full flex-1 md:col-start-1 md:row-start-1 md:h-full md:min-h-0 md:flex-none">
                <PairChoiceCard
                  option={displayA}
                  slotVisual={slotVisualA}
                  showPendingShimmerOverlay={showPendingShimmerA}
                  disabled={cardsDisabled}
                  onChoose={() => {
                    if (session?.currentPair?.optionAId) {
                      void handleChooseOption(session.currentPair.optionAId)
                    }
                  }}
                />
              </div>

              <div className="flex w-full min-w-0 flex-none md:col-span-2 md:row-start-2">
                <button
                  type="button"
                  className={cn(
                    'flex h-14 w-full items-center justify-center gap-2 rounded-2xl text-base font-medium transition-colors',
                    neitherWorking
                      ? 'cursor-default bg-card text-muted-foreground'
                      : 'bg-card text-foreground hover:bg-muted/50 active:scale-[0.98]',
                    cardsDisabled ? 'pointer-events-none' : '',
                  )}
                  disabled={cardsDisabled}
                  aria-busy={neitherWorking}
                  onClick={() => void handleChooseOption('skip')}
                >
                  {neitherWorking ? (
                    <Loader2 className="h-5 w-5 shrink-0 animate-spin text-muted-foreground motion-reduce:animate-none" aria-hidden />
                  ) : null}
                  {!neitherWorking ? 'Neither' : <span className="sr-only">Loading choices</span>}
                </button>
              </div>

              <div className="flex min-h-0 min-w-0 w-full flex-1 md:col-start-2 md:row-start-1 md:h-full md:min-h-0 md:flex-none">
                <PairChoiceCard
                  option={displayB}
                  slotVisual={slotVisualB}
                  showPendingShimmerOverlay={showPendingShimmerB}
                  disabled={cardsDisabled}
                  onChoose={() => {
                    if (session?.currentPair?.optionBId) {
                      void handleChooseOption(session.currentPair.optionBId)
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <MatcherExitDialog
          open={isExitDialogOpen}
          onOpenChange={setIsExitDialogOpen}
          onLeave={exitToMatcherLobby}
          isLeaving={isExitActionPending}
          description={
            isAuthenticated
              ? 'Your progress is already saved to your account. You can leave now and reopen it later.'
              : 'You are not signed in. If you leave now, you may not be able to resume this session later.'
          }
        />
      </>
    )
  }

  if (flowStep === 'preferences') {
    const isSpecific = intentMode === 'specific'

    return (
      <>
        <div className="flex flex-col bg-background transition-colors overflow-hidden min-h-[100dvh] max-h-[100dvh] md:min-h-[calc(100dvh-57px)] md:max-h-[calc(100dvh-57px)]">
          {renderTopBar('2. Refining your search', 'Choose your location — a specific city or anyone in a country we serve.')}

          <div className={cn(MATCHER_PHONE_GUTTER, 'flex-1 min-h-0 overflow-y-auto pt-2 pb-safe space-y-4')}>
            <div className="flex flex-col gap-3">
              <label className="text-sm font-medium" htmlFor="matcher-location-picker">
                Location
              </label>
              <MatcherFeedLocationPicker
                selectedCity={selectedCity}
                onSaved={setSelectedCity}
                disabled={cities.length === 0}
              />
            </div>

            <div className="flex flex-col gap-3">
              <label className="text-sm font-medium">What best describes you?</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                    intentMode === 'specific' ? 'border-primary bg-primary/10' : 'border-border bg-card'
                  }`}
                  onClick={() => setIntentMode('specific')}
                >
                  <p className="font-medium">I have a specific idea</p>
                  <p className="text-xs text-muted-foreground mt-1">Share a short brief for your artist</p>
                </button>
                <button
                  type="button"
                  className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                    intentMode === 'exploring' ? 'border-primary bg-primary/10' : 'border-border bg-card'
                  }`}
                  onClick={() => setIntentMode('exploring')}
                >
                  <p className="font-medium">I’m just looking</p>
                  <p className="text-xs text-muted-foreground mt-1">Open to artist suggestions</p>
                </button>
              </div>
            </div>

            {isSpecific ? (
              <div className="flex flex-col gap-3">
                <label className="text-sm font-medium">
                  Describe your idea to the artist <span className="text-muted-foreground">(optional)</span>
                </label>
                <textarea
                  value={ideaText}
                  onChange={(event) => setIdeaText(event.target.value)}
                  className="w-full min-h-28 rounded-xl border border-border bg-background p-3 text-sm"
                  placeholder="Placement, style, size, or references..."
                />
              </div>
            ) : null}

            <Button
              className="w-full h-12"
              disabled={!selectedCity}
              onClick={() => void goToResults()}
            >
              Show my matches
            </Button>
          </div>
        </div>

        <MatcherExitDialog
          open={isExitDialogOpen}
          onOpenChange={setIsExitDialogOpen}
          onLeave={exitToMatcherLobby}
          isLeaving={isExitActionPending}
          description={
            isAuthenticated
              ? 'Your progress is already saved to your account. You can leave now and reopen it later.'
              : 'You are not signed in. If you leave now, you may not be able to resume this session later.'
          }
        />
      </>
    )
  }

  return (
    <>
      <div className="min-h-screen bg-background transition-colors pb-safe">
        <div className={cn(MATCHER_PHONE_GUTTER, 'pt-safe-native-top md:pt-4 pb-4 space-y-4')}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">3. Recommended artists</h1>
              <p className="text-foreground mt-2">
                {intentMode === 'exploring'
                  ? 'Click an artist to see their profile.'
                  : 'Click an artist to enter a chat. Your results are saved and you can always return to them later.'}
              </p>
              {!isAuthenticated ? (
                <div className="mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSaveAfterLoginRequested(true)
                      setShowAuthModal(true)
                    }}
                  >
                    Save results
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">Sign in to save these results for later.</p>
                </div>
              ) : null}
              {cityFallbackApplied ? (
                <p className="text-xs text-muted-foreground mt-2">
                  No direct city matches found, showing your best overall fits.
                </p>
              ) : null}
            </div>
            <button
              type="button"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-background text-foreground"
              onClick={handleExitButtonClick}
              aria-label="Close matcher"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {resultsError ? (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              <p>{resultsError}</p>
              <Button className="mt-3" variant="outline" onClick={() => void loadResultsForCurrentSession()}>
                Retry
              </Button>
            </div>
          ) : null}

          {isLoadingResults ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="rounded-2xl border border-border/70 bg-card p-3 animate-pulse h-44" />
              ))}
            </div>
          ) : null}

          {!isLoadingResults && !resultsError && resultCards.length === 0 ? (
            <div className="rounded-2xl border border-border/70 bg-card p-5 text-sm text-muted-foreground">
              No matches available yet. Please try again.
            </div>
          ) : null}

          {!isLoadingResults && !resultsError && resultCards.length > 0 ? (
            <div className="space-y-2">
              {resultCards.map((entry) => (
                <div
                  key={entry.artist.id}
                  className="relative overflow-hidden bg-card rounded-2xl p-2 border border-border/70 shadow-sm cursor-pointer"
                  onClick={() => {
                    const matcherParams = buildMatcherNavigationParams()
                    const destination = intentMode === 'exploring' ? 'profile' as const : 'chat' as const

                    if (session?.sessionId) {
                      void trackEvent('matcher_result_artist_clicked', {
                        session_id: session.sessionId,
                        artist_id: entry.artist.id,
                        rank: entry.match.rank,
                        intent_mode: intentMode,
                        destination,
                      })
                    }

                    if (destination === 'profile') {
                      matcherParams.set('source', 'pairwise_matcher_profile')
                      navigate(`/artist/${entry.artist.id}?${matcherParams.toString()}`)
                      return
                    }

                    const chatParams = new URLSearchParams({
                      artistId: entry.artist.id,
                      source: 'pairwise_matcher',
                    })

                    matcherParams.forEach((value, key) => {
                      chatParams.set(key, value)
                    })

                    if (selectedCity) {
                      chatParams.set('city', selectedCity)
                    }

                    if (ideaText.trim()) {
                      chatParams.set('idea', ideaText.trim())
                    }

                    navigate(`/chats/new?${chatParams.toString()}`)
                  }}
                >
                  <div className="absolute right-3 top-3 z-20 rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground shadow-sm">
                    #{entry.match.rank}
                  </div>

                  <div className="relative z-10 flex items-center gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {entry.artist.avatarUrl || entry.artist.compressedAvatarUrl ? (
                        <img
                          src={entry.artist.compressedAvatarUrl || entry.artist.avatarUrl}
                          alt={getArtistDisplayName(entry.artist)}
                          className="w-16 h-16 rounded-2xl object-cover border-4 border-border"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center border-4 border-border">
                          <span className="text-lg font-medium">{entry.artist.displayName?.[0] || '?'}</span>
                        </div>
                      )}

                      <div className="flex-1 min-w-0 flex flex-col justify-center">
                        <h3 className="font-medium text-lg leading-tight truncate">{getArtistDisplayName(entry.artist)}</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          {entry.artist.location?.city ? entry.artist.location.city : ''}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="relative z-10 mt-2 grid grid-cols-3 md:grid-cols-4 gap-1.5">
                    {(entry.previews.length > 0 ? entry.previews : [...Array(4)]).slice(0, 4).map((item, index) => {
                      if (!item || !('id' in item)) {
                        return (
                          <div
                            key={`placeholder-${index}`}
                            className={`aspect-square bg-muted rounded-xl ${index >= 3 ? 'hidden md:block' : ''}`}
                          />
                        )
                      }

                      return (
                        <img
                          key={item.id}
                          src={item.compressedImageUrl || item.imageUrl}
                          alt={item.caption || 'Portfolio item'}
                          className={`aspect-square w-full object-cover rounded-xl ${index >= 3 ? 'hidden md:block' : ''}`}
                        />
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <MatcherExitDialog
        open={isExitDialogOpen}
        onOpenChange={setIsExitDialogOpen}
        onLeave={exitToMatcherLobby}
        isLeaving={isExitActionPending}
        description={
          isAuthenticated
            ? 'Your results are already saved to your account. You can leave now and reopen them later.'
            : 'Your results are only on this device until you sign in. You can leave now and come back to this screen later.'
        }
      />

      <AuthModal
        isOpen={showAuthModal}
        onOpenChange={setShowAuthModal}
        onSuccess={() => {
          setShowAuthModal(false)
        }}
      />
    </>
  )
}
